import { useState } from 'preact/hooks'
import { FieldText } from './FieldText'
import { AccountFitClubCard } from './AccountFitClubCard'
import { FieldSwitch } from './FieldSwitch'
import { useFormInput } from '../hooks/useFormInput'
import register from 'preact-custom-element'
import { FieldTel } from './FieldTel'
import { FieldEmail } from './FieldEmail'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'
import { formatMobile } from '@/components/helpers/utils'
import { accountTranslations } from '../helpers/translations'
import { PageBackButton } from './AccountSectionWrapper'
import Icon from '@/components/snippets/icon/icon'

const ACCOUNT_SIGN_IN_STATES = {
  INIT: 'INIT',
  MOBILE_CODE: 'MOBILE_CODE',
  DETAILS: 'DETAILS',
  SUCCESS: 'SUCCESS'
}

/**
 * login_requested fires when a code/magic-link is sent to an existing customer.
 * The actual login completion is tracked separately in account-portal.liquid,
 * since loginWithSMS/mobileLogin and the email magic-link both finish via a
 * redirect back to the site rather than a JS callback on this page.
 *
 * Routed through BCDataLayerManager.manager.push() rather than a raw
 * window.dataLayer.push() so it gets the same brand event prefix (e.g. bc_)
 * and event_id/event_timestamp fields every other custom event gets - these
 * fire on user interaction, well after page load, so the manager is reliably
 * available by then.
 */
const pushDataLayer = (eventName, data) => {
  window.BCDataLayerManager?.manager?.push(eventName, data)
}

/**
 *
 * @param {string} value
 */
const handleValue = (value) => {
  const key = value.includes('@') ? 'email' : 'mobile_phone'
  return {
    [key]: value
  }
}

const AccountMobileCodeValidation = () => {
  const [code, setCode] = useState()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    if (!code) {
      setSubmitting(false)
      return
    }
    const response = await window.omneo.portal.loginWithSMS(code)
    const token = response?.data?.token
    window.omneo.portal.mobileLogin(token)
  }
  return (
    <form
      data-ui={submitting ? 'visible' : ''}
      onSubmit={handleSubmit}
      className={'relative flex w-full flex-col gap-4 p-2 transition-opacity group-data-visible/login:opacity-45'}
    >
      <p className="paragraph-md">
          {accountTranslations.account_portal.sign_up_mobile_code_description}
        </p>
      <div className={'w-11/12'}>
        <FieldText label={'Code'} inputMode={'numeric'} placeholder={'Enter login code'} autoComplete='one-time-code' onChange={(e) => setCode(e.currentTarget.value)} value={code} />
      </div>
      <button className={'btn btn-md btn-solid-black btn-inline relative w-fit overflow-hidden px-4 py-2'}>
        <span className={`${submitting ? 'invisible' : 'visible'}`}>Login</span>
        <LoaderSpin wrapperClass={`bg-black ${submitting ? 'visible' : 'invisible'}`} />
      </button>{' '}
    </form>
  )
}

const AccountSignUpForm = ({ props }) => {
  const [submitting, setSubmitting] = useState(false)
  const { formInputs, handleOnChange } = useFormInput({ value: '' })

  /** Note Mobile flow still needs designs as it stands this flows only requires
   * the code input field. If they want to display the card on mobile login
   * that information is available from the checkAvailability endpoint.
   *
   *
   */
  const mobileAccountFlow = async (mobilePhoneAvailable) => {
    if (mobilePhoneAvailable) {
      props.handleStateChange(ACCOUNT_SIGN_IN_STATES.DETAILS)
    } else {
      const mobilePhone = formatMobile(formInputs.value.value)
      await window.omneo.portal.getSMSAuthCode(mobilePhone)
      pushDataLayer('login_requested', { method: 'sms' })
      props.handleStateChange(ACCOUNT_SIGN_IN_STATES.MOBILE_CODE)
    }
  }

  /**
   *
   * If the email address is available for use the Details state (Sign up) will be set.
   * Otherwise display the accounts Loyalty card information
   *
   * @param {*} customer
   * @param {*} value
   */
  const emailAccountFlow = async (customer, value) => {
    if (customer?.email?.available) {
      props.handleUpdateCustomer({
        email: value
      })
      props.handleStateChange(ACCOUNT_SIGN_IN_STATES.DETAILS)
    } else {
      await window.omneo.portal.sendLoginEmail(formInputs.value.value, window.location.pathname)
      pushDataLayer('login_requested', { method: 'email' })
      props.handleUpdateCustomer({
        id: customer.loyaltyCard,
        name: customer.email.profile?.full_name
      })
      props.handleStateChange(ACCOUNT_SIGN_IN_STATES.SUCCESS)
    }
  }

  async function handleSubmit(event) {
    event.preventDefault()
    document.querySelector('[data-role="sign-up-info"]').remove()
    setSubmitting(true)

    if (!window.omneo.portal) return
    const value = formInputs.value.value
    const body = handleValue(value)
    const key = value.includes('@') ? 'email' : 'mobile_phone'

    // Remove spaces and format mobile phone
    if (key === 'mobile_phone') {
      body.mobile_phone = formatMobile(body.mobile_phone)
    }

    const { data } = await window.omneo.portal.checkAvailability(body)
    if (key === 'mobile_phone') {
      await mobileAccountFlow(data.availability.mobile_phone.available, value)
      return
    }
    await emailAccountFlow(data.availability, value)

    // setSubmitting(false)
  }
  return (
    <form
      data-ui={submitting ? 'visible' : ''}
      onSubmit={handleSubmit}
      className={
        'group/login relative flex h-full w-full flex-col gap-4 p-2 transition-opacity group-data-visible/login:opacity-45'
      }
    >
      <div className={'w-11/12'}>
        <FieldText
          onChange={handleOnChange}
          value={formInputs.value.value}
          name={formInputs.value.key}
          type={'text'}
          placeholder="Enter email or mobile number"
          autoComplete={'email'}
        />
      </div>
      <button className={'btn btn-md btn-solid-black btn-inline relative w-fit overflow-hidden px-4 py-2'}>
        <span className={`${submitting ? 'invisible' : 'visible'}`}>Continue</span>
        <LoaderSpin wrapperClass={`bg-black ${submitting ? 'visible' : 'invisible'}`} />
      </button>
      {/* The child element is the Character snippet render from liquid. */}
      {props.children}
    </form>
  )
}

/**
 * Need to check how to we can gain access to the profile details
 *
 * @param {*} param0
 * @returns
 */
const AccountSignUpSuccess = ({ props }) => {
  return (
    <div className={'flex flex-col gap-4'}>
      <div className={'flex flex-col gap-4'}>
        <p className={'paragraph-lg font-bold'}>{accountTranslations.account_portal.sign_up_success_title}</p>
        <p className="paragraph-md">
          {props.config.customer?.id
            ? accountTranslations.account_portal.sign_up_success_description
            : accountTranslations.account_portal.sign_up_not_activated_description}
        </p>
      </div>
      {props.config.customer?.id && (
        <AccountFitClubCard
          memberSince={''}
          firstName={props.config.customer.name}
          barcode={props.config.customer.id}
          showTotal={false}
        />
      )}
    </div>
  )
}

const AccountSignUpDetails = ({ props }) => {
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const { formInputs, handleOnChange } = useFormInput({
    email: props.config.customer.email || '',
    firstName: '',
    lastName: '',
    mobile: '',
    postCode: '',
    emailOptIn: false,
    smsOptIn: false,
    gender: '',
    date: ''
  })

  async function handleSubmit(event) {
    setError(null)
    setSubmitting(true)

    if (!window.omneo.portal) return
    event.preventDefault()

    const formData = new FormData(event.currentTarget)

    const elements = event.currentTarget.elements
    Array.from(elements).map((ele) => {
      const hasPattern = ele.hasAttribute('pattern')
      if (!hasPattern) return undefined

      const pattern = ele.getAttribute('pattern')
      const matches = ele.value.match(pattern.replaceAll('/', ''))
      return ele
    })

    const obj = {}
    formData.forEach((value, key) => {
      obj[key] = value
    })

    const mobile = formatMobile(formInputs.mobile.value)

    const accountData = {
      email: formInputs.email.value,
      first_name: formInputs.firstName.value,
      last_name: formInputs.lastName.value,
      mobile_phone: mobile,
      custom_attributes: [
        {
          namespace: 'details',
          handle: 'postcode',
          type: 'string',
          value: formInputs.postCode.value
        }
      ],
      attributes: {
        comms: {
          email_promo: formInputs.emailOptIn.value,
          email_benefits: formInputs.emailOptIn.value,
          email_reminders: formInputs.emailOptIn.value,
          email_service: formInputs.emailOptIn.value,
          email_optout: !formInputs.emailOptIn.value,
          sms_promo: formInputs.smsOptIn.value,
          sms_benefits: formInputs.smsOptIn.value,
          sms_reminders: formInputs.smsOptIn.value,
          sms_service: formInputs.smsOptIn.value,
          sms_optout: !formInputs.smsOptIn.value
        }
      }
    }

    const { data, errors } = await window.omneo.portal.createAccount(accountData)

    if (errors) {
      switch (true) {
        case Object.keys(errors).includes('mobile_phone'): {
          setError({ type: 'mobile_phone', message: errors.mobile_phone[0] ?? 'Error' })
          break
        }
        case Object.keys(errors).includes('email'): {
          setError({ type: 'email', message: errors.email[0] ?? 'Error' })
          break
        }
        default: {
          setError({ type: 'error', message: errors.error[0] ?? accountTranslations.account_portal.sign_up_generic_error })
          break
        }
      }
      setSubmitting(false)
    }

    if (!data) return

    pushDataLayer('sign_up', {})

    props.handleUpdateCustomer({
      id: data.identity,
      name: [data.first_name || '', data.last_name || ''].join(' ')
    })

    localStorage.setItem('isFirstLogin', 'true')
    props.handleStateChange(ACCOUNT_SIGN_IN_STATES.SUCCESS)
  }

  return (
    <div className={'absolute inset-0 h-full w-full bg-neutrals-base p-4'}>
      <div className={'py-4 flex gap-2'}>
        <button onClick={() => props.handleStateChange(ACCOUNT_SIGN_IN_STATES.INIT)} className={'flex flex-row gap-2'}>
          <Icon id="arrow-left" className="my-auto" />
        </button>
        <p className={'paragraph-lg font-bold'}>{props.dataSignUpTitle}</p>
      </div>
      <form onSubmit={handleSubmit} className={'flex flex-col gap-4'}>
        <FieldEmail
          label={accountTranslations.form_labels.email}
          autoComplete="email"
          placeholder={accountTranslations.form_placeholders.email}
          required={true}
          name={formInputs.email.key}
          value={formInputs.email.value}
          onChange={handleOnChange}
          error={error?.type === 'email' ? error.message : null}
        />

        <div className={'flex flex-row gap-4'}>
          <FieldText
            type="text"
            autoComplete="given-name"
            label={accountTranslations.form_labels.first_name}
            required={true}
            placeholder={accountTranslations.form_placeholders.first_name}
            name={formInputs.firstName.key}
            value={formInputs.firstName.value}
            onChange={handleOnChange}
          />
          <FieldText
            type="text"
            autoComplete="family-name"
            label={accountTranslations.form_labels.last_name}
            required={true}
            placeholder={accountTranslations.form_placeholders.last_name}
            name={formInputs.lastName.key}
            value={formInputs.lastName.value}
            onChange={handleOnChange}
          />
        </div>
        <FieldTel
          label={accountTranslations.form_labels.mobile}
          autoComplete="tel"
          type="tel"
          placeholder={accountTranslations.form_placeholders.mobile}
          required={true}
          name={formInputs.mobile.key}
          value={formInputs.mobile.value}
          onChange={handleOnChange}
          error={error?.type === 'mobile_phone' ? error.message : null}
        />
        <FieldText
          type="text"
          autoComplete="postal-code"
          label={accountTranslations.form_labels.postcode}
          required
          placeholder={accountTranslations.form_placeholders.postcode}
          name={formInputs.postCode.key}
          value={formInputs.postCode.value}
          onChange={handleOnChange}
        />
        <div className={'paragraph-sm my-auto font-bold'}>{accountTranslations.form_labels.communications}</div>
        <div className={'flex gap-4'}>
          <FieldSwitch
            onChange={handleOnChange}
            name={formInputs.emailOptIn.key}
            value={formInputs.emailOptIn.value}
            options={[
              {
                label: accountTranslations.form_labels.email_opt_in,
                value: 'email'
              }
            ]}
          />
          <FieldSwitch
            onChange={handleOnChange}
            name={formInputs.smsOptIn.key}
            value={formInputs.smsOptIn.value}
            options={[
              {
                label: accountTranslations.form_labels.sms_opt_in,
                value: 'sms'
              }
            ]}
          />
        </div>
        <div>
          <div
            className={'richtext richtext-accent-url paragraph-xs text-neutrals-secondary-grey'}
            dangerouslySetInnerHTML={{ __html: props.dataTerms }}
          ></div>
        </div>
        <button className={'btn btn-solid-black btn-md mt-2 w-full justify-center relative'}>
          {submitting ? <LoaderSpin /> : accountTranslations.form_labels.continue}
        </button>
        {error?.message && <div className={'paragraph-sm text-system-error font-medium'}>{error.message}</div>}
      </form>
    </div>
  )
}

const AccountSignUpBody = ({ props }) => {
  switch (props.config.state) {
    case ACCOUNT_SIGN_IN_STATES.INIT: {
      return <AccountSignUpForm props={props} />
    }
    case ACCOUNT_SIGN_IN_STATES.MOBILE_CODE: {
      return <AccountMobileCodeValidation />
    }
    case ACCOUNT_SIGN_IN_STATES.DETAILS: {
      return <AccountSignUpDetails props={props} />
    }
    case ACCOUNT_SIGN_IN_STATES.SUCCESS: {
      return <AccountSignUpSuccess props={props} />
    }
  }
}

export const AccountSignUp = (props) => {
  const [config, setConfig] = useState({
    state: ACCOUNT_SIGN_IN_STATES.INIT,
    customer: {}
  })

  /** Used on a click event and pulls the data-role attribute
   * from element to get the next state.
   */
  const handleStateChange = (state) => {
    setConfig((c) => ({
      ...c,
      state
    }))
  }

  const handleUpdateCustomer = (customer) => {
    setConfig((c) => ({
      ...c,
      customer
    }))
  }

  return (
    <div className={'h-full p-4'}>
      <AccountSignUpBody
        props={{
          ...props,
          handleStateChange,
          handleUpdateCustomer,
          config
        }}
      />
    </div>
  )
}

register(AccountSignUp, 'x-account-sign-in')
